import { USDV_MINT } from "@shared/tokens/usdv";
import { RPC_ENDPOINT } from "@solomon-apps/config";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

export interface UsdvSupplyData {
    totalSupply: number;
    rawAmount: string;
    decimals: number;
    slot: number;
}

interface TokenSupplyRpcResponse {
    error?: { message?: string };
    result?: {
        context?: { slot?: number };
        value?: {
            amount?: string;
            decimals?: number;
            uiAmountString?: string;
        };
    };
}

export async function fetchUsdvSupply(signal?: AbortSignal): Promise<UsdvSupplyData> {
    const response = await fetch(RPC_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            jsonrpc: "2.0",
            id: 1,
            method: "getTokenSupply",
            params: [USDV_MINT]
        }),
        signal
    });

    if (!response.ok) {
        throw new Error(`USDv supply RPC HTTP ${response.status}`);
    }

    const json = (await response.json()) as TokenSupplyRpcResponse;
    if (json.error) {
        throw new Error(json.error.message ?? "USDv supply RPC failed");
    }

    const rawAmount = json.result?.value?.amount;
    const decimals = json.result?.value?.decimals;
    const uiAmountString = json.result?.value?.uiAmountString;
    const slot = json.result?.context?.slot;
    const totalSupply = Number(uiAmountString);

    if (
        !rawAmount ||
        decimals === undefined ||
        slot === undefined ||
        !Number.isFinite(totalSupply) ||
        totalSupply < 0
    ) {
        throw new Error("USDv supply RPC returned an invalid response");
    }

    return { totalSupply, rawAmount, decimals, slot };
}

export function useUsdvSupply(enabled = true): UseQueryResult<UsdvSupplyData> {
    return useQuery<UsdvSupplyData>({
        queryKey: ["usdv-total-supply", USDV_MINT],
        queryFn: ({ signal }) => fetchUsdvSupply(signal),
        staleTime: 60 * 1000,
        refetchInterval: 60 * 1000,
        retry: 3,
        enabled,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true
    });
}
